import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Carousel } from '@openx/components-core/lib/Carousel/Carousel';
import { dealPromptFlags } from '@openx/constants/feature-flag-keys';
import type {
  CumulativeBundleSegment,
  CumulativeDomainSegment,
  InventoryBundle,
  InventoryDomain,
} from '@openx/types/gqlTypes';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { promptModes } from '../constants';
import { useDealPrompt } from '../hooks/useDealPrompt';
import { useDealPromptCreateDeal } from '../hooks/useDealPromptCreateDeal';
import { calculateMetricsDeviation } from '../utils/calculateMetricsDeviation';
import { useDealPromptDomainsContext } from '../utils/context/DealPromptDomainsContext';
import {
  type AudienceSegmentSetWithSegments,
  getAudienceTilesData,
  getTilesData,
  isAudienceSegmentSetWithSegmentsTile,
  toAudienceSegmentSet,
} from '../utils/getTilesData';
import { isExplicitNonUsRegion } from '../utils/isExplicitNonUsRegion';
import { createNormalizedDefinitionFromSegmentSetAndSelection } from '../utils/normalizedDefinitionFromAudienceItems';
import { dealPromptContextTypes, type ResultsTileRightItem, type ResultsTileStat } from '../utils/types';
import { DealPromptAudienceCreateDealDialog } from './DealPromptAudienceCreateDealDialog';
import { formatValue } from './details/utils';
import { getBadgeConfig } from './tiles/const';
import { DealPromptResultsTile } from './tiles/DealPromptResultsTile';
import { DealPromptTile } from './tiles/DealPromptTile';

export const DealPromptResults = () => {
  const {
    state: { lastSubmittedFilterCriteria, mode, contextType },
    openDetails,
    openDetailsForAudience,
  } = useDealPrompt();
  const { cumulativeData, audienceSegmentsSets, audienceSegments } = useDealPromptDomainsContext();
  const { isAudienceCreateDealOpen, handleCreateDeal, closeAudienceCreateDealDialog, submitAudienceCreateDeal } =
    useDealPromptCreateDeal();
  const [selectedAudienceItem, setSelectedAudienceItem] = useState<AudienceSegmentSetWithSegments | null>(null);
  const { t } = useTranslation();
  const tiles = getTilesData(cumulativeData ?? [], t, lastSubmittedFilterCriteria);
  const audienceTiles = getAudienceTilesData(audienceSegmentsSets ?? [], audienceSegments, t);
  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isInAllowListForSingleTileAppVersion = isGrowthbookFeatureOn(
    dealPromptFlags.DEAL_PROMPT_SINGLE_TILE_APP_VERSION_ALLOWLIST
  );

  const isEditing = mode === promptModes.EDIT_MODE;

  const isResultsUIV2Enabled = isGrowthbookFeatureOn(dealPromptFlags.DEAL_PROMPT_RESULTS_UI_V2);
  const isAudienceContext = contextType === dealPromptContextTypes.AUDIENCE;
  const isDomainsContext = contextType === dealPromptContextTypes.DOMAINS;
  const shouldUseNewUI = isResultsUIV2Enabled;

  const badgeConfig = getBadgeConfig(
    contextType === dealPromptContextTypes.ALL ? dealPromptContextTypes.DOMAINS : contextType,
    t
  );
  const bestMatchItem = (tiles[1]?.item ?? null) as CumulativeDomainSegment | CumulativeBundleSegment | null;
  const nonAudienceItemResults = useMemo(
    () =>
      (tiles[1].item as CumulativeDomainSegment | CumulativeBundleSegment)?.results?.filter(Boolean) as
        | InventoryDomain[]
        | InventoryBundle[],
    [tiles]
  );
  const nonAudienceResultsPreviewContent: ResultsTileRightItem[] = useMemo(
    () =>
      (nonAudienceItemResults || []).map(result => ({
        label: 'appName' in result ? result?.appName : result?.domainName,
        value: result?.relevance || 0,
        formattedValue: formatValue('relevance', result?.relevance || 0),
      })),
    [nonAudienceItemResults]
  );

  const { allMetrics } =
    useMemo(
      () => calculateMetricsDeviation(nonAudienceItemResults ?? [], nonAudienceItemResults ?? []),
      [nonAudienceItemResults]
    ) || {};

  const stats: ResultsTileStat[] = [
    {
      label: t('Relevance'),
      value: formatValue('relevance', allMetrics?.relevance || 0),
      dataTest: 'results-metric-relevance',
    },
    {
      label: t('Market Requests'),
      value: formatValue('totalCprMarketRequests', allMetrics?.totalCprMarketRequests || 0),
      dataTest: 'results-metric-market-requests',
    },
    {
      label: t('Average CPM'),
      value: formatValue('avgBidCpm', allMetrics?.avgBidCpm || 0),
      dataTest: 'results-metric-average-cpm',
    },
    {
      label: t('Viewability'),
      value: formatValue('avgViewability', allMetrics?.avgViewability || 0),
      dataTest: 'results-metric-viewability',
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const correctTileData = useMemo(
    () => (contextType === dealPromptContextTypes.AUDIENCE ? audienceTiles : tiles),
    [contextType, audienceTiles, tiles]
  );

  const handleAudienceCreateDeal = useCallback(
    (item: AudienceSegmentSetWithSegments) => {
      setSelectedAudienceItem(item);
      handleCreateDeal(item);
    },
    [handleCreateDeal]
  );

  const handleReviewNonAudienceDetails = useCallback(() => {
    if (bestMatchItem) openDetails({ title: tiles[1].title, item: bestMatchItem });
  }, [openDetails, tiles, bestMatchItem]);

  const handleCreateNonAudienceDeal = useCallback(() => {
    if (bestMatchItem) handleCreateDeal(bestMatchItem);
  }, [handleCreateDeal, bestMatchItem]);

  const handleSubmitAudienceCreateDeal = useCallback(
    async (form: Parameters<typeof submitAudienceCreateDeal>[0]['form']) => {
      if (!selectedAudienceItem) {
        return;
      }
      await submitAudienceCreateDeal({
        form,
        segmentSet: toAudienceSegmentSet(selectedAudienceItem),
        selectedSegments: selectedAudienceItem.segments,
      });
    },
    [selectedAudienceItem, submitAudienceCreateDeal]
  );

  const isAudienceSelectionExplicitNonUsRegion = useMemo(
    () =>
      selectedAudienceItem
        ? isExplicitNonUsRegion(
            createNormalizedDefinitionFromSegmentSetAndSelection(selectedAudienceItem, selectedAudienceItem.segments)
          )
        : false,
    [selectedAudienceItem]
  );

  const audienceCarouselTiles = useMemo(
    () =>
      audienceTiles
        .filter(tile => tile.item && isAudienceSegmentSetWithSegmentsTile(tile.item))
        .map(tile => {
          const item = tile.item as AudienceSegmentSetWithSegments;
          const metrics = item.metrics;

          const tileStats: ResultsTileStat[] = [
            {
              label: t('Unique Users'),
              value: abbreviateNumericValue(metrics?.reach ?? 0, { decimalPlaces: 2 }),
              dataTest: 'unique-users',
            },
            {
              label: t('Avg CPM'),
              value: `$${abbreviateNumericValue(metrics?.avgCPM ?? 0, { decimalPlaces: 2 })}`,
              dataTest: 'avg-cpm',
            },
            {
              label: t('Relevance'),
              value: `${abbreviateNumericValue(metrics?.relevance ?? 0, { decimalPlaces: 0 })}%`,
              dataTest: 'relevance',
            },
            {
              label: t('Active IPs'),
              value: abbreviateNumericValue(metrics?.activeIPs ?? 0, { decimalPlaces: 2 }),
              dataTest: 'active-ips',
            },
          ];

          const segmentItems: ResultsTileRightItem[] = item.segments.map(segment => ({
            label: segment.name,
            value: segment.reach,
            formattedValue: abbreviateNumericValue(segment.reach, { decimalPlaces: 2 }),
            dataTest: `segment-${segment.id}`,
          }));

          return { tile, item, tileStats, segmentItems };
        }),
    [audienceTiles, t]
  );

  return (
    <Stack
      data-test="deal-prompt-domains-results-container"
      sx={{ backgroundColor: 'background.grayLight', width: '100%', height: '100%', alignItems: 'center' }}
    >
      <Box sx={{ width: '100%', maxWidth: shouldUseNewUI ? '906px' : '1208px' }}>
        <Typography sx={{ pt: 4, pl: shouldUseNewUI ? 0 : 4, fontWeight: 'fontWeightBold', fontSize: '18px' }}>
          {t('Results:')}
        </Typography>

        <Grid
          container
          spacing={4}
          sx={{
            gridTemplateColumns: { xs: '1fr', sm: `repeat(2, 1fr)`, md: `repeat(3, 1fr)` },
            px: shouldUseNewUI ? 0 : 4,
            pt: 4,
            pb: 4,
            ...(isEditing && { opacity: 0.64 }),
          }}
          data-test="deal-prompt-domains-results-grid"
        >
          {shouldUseNewUI && isAudienceContext && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Carousel maxWidth={906}>
                {audienceCarouselTiles.map(({ tile, item, tileStats, segmentItems }) => (
                  <DealPromptResultsTile
                    key={tile.title}
                    title={tile.title}
                    badge={badgeConfig}
                    stats={tileStats}
                    rightSectionTitle={t('Segments')}
                    rightSectionItems={segmentItems}
                    rightSectionMaxItems={6}
                    reviewSetDetailsLabel={t('Review segments')}
                    onReviewSetDetails={() => openDetailsForAudience({ title: tile.title, item })}
                    onCreateDeal={() => handleAudienceCreateDeal(item)}
                    dataTest={`deal-prompt-audience-tile-${tile.title}`}
                    createDealPendoFeature={`deal-prompt-results-${contextType}-create-deal-${tile.title.toLowerCase().replace(/\s+/g, '-')}`}
                    reviewSetDetailsPendoFeature={`deal-prompt-results-${contextType}-customize-set-${tile.title.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                ))}
              </Carousel>
            </Box>
          )}

          {shouldUseNewUI && !isAudienceContext && (
            <DealPromptResultsTile
              title={t('Best match for your campaign')}
              reviewSetDetailsLabel={isDomainsContext ? t('Review domains') : t('Review apps')}
              badge={badgeConfig}
              stats={stats}
              rightSectionTitle={isDomainsContext ? t('Most relevant domains') : t('Most relevant apps')}
              rightSectionItems={nonAudienceResultsPreviewContent}
              rightSectionMaxItems={6}
              rightSectionMaxValue={100}
              barColor={badgeConfig.color}
              onReviewSetDetails={handleReviewNonAudienceDetails}
              onCreateDeal={handleCreateNonAudienceDeal}
              createDealPendoFeature={`deal-prompt-results-${contextType}-create-deal-best-match`}
              reviewSetDetailsPendoFeature={`deal-prompt-results-${contextType}-customize-set-best-match`}
            />
          )}

          {!shouldUseNewUI &&
            (isInAllowListForSingleTileAppVersion && correctTileData?.[0] ? (
              <DealPromptTile key={correctTileData[0].title} {...correctTileData[0]} />
            ) : (
              correctTileData.map(tile => <DealPromptTile key={tile.title} {...tile} />)
            ))}
        </Grid>
      </Box>

      {isAudienceCreateDealOpen && selectedAudienceItem && (
        <DealPromptAudienceCreateDealDialog
          open={isAudienceCreateDealOpen}
          onClose={closeAudienceCreateDealDialog}
          isExplicitNonUsRegion={isAudienceSelectionExplicitNonUsRegion}
          onSubmit={handleSubmitAudienceCreateDeal}
        />
      )}
    </Stack>
  );
};
